import React, { useState } from 'react';
import ModalCloseButton from './ModalCloseButton';
import '../styles/components/deleteAccountModal.css';

interface DeleteAccountModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
}

const DeleteAccountModal: React.FC<DeleteAccountModalProps> = ({ isOpen, onClose, onConfirm }) => {
  const [confirmText, setConfirmText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleConfirm = async () => {
    if (confirmText !== 'DELETE') {
      setError('Please type DELETE to confirm');
      return;
    }

    setIsDeleting(true);
    setError(null);

    try {
      await onConfirm();
      // onConfirm will handle logout and navigation
    } catch (err: any) {
      setError(err.message || 'Failed to delete account');
      setIsDeleting(false);
    }
  };

  const handleClose = () => {
    if (!isDeleting) {
      setConfirmText('');
      setError(null);
      onClose();
    }
  };

  return (
    <div className="delete-account-modal-overlay" onClick={handleClose}>
      <div className="delete-account-modal" onClick={(e) => e.stopPropagation()}>
        <div className="delete-account-modal-header">
          <h2>Delete Your Account?</h2>
          <ModalCloseButton onClick={handleClose} />
        </div>

        <div className="delete-account-modal-body">
          <div className="delete-account-warning">
            ⚠️ This action cannot be undone
          </div>

          <div className="delete-account-info">
            <p><strong>Your account will be scheduled for deletion.</strong></p>
            <p>All of your data will be permanently deleted in 30 days:</p>
            <ul>
              <li>All your items</li>
              <li>All your collections</li>
              <li>All your shared groups</li>
              <li>All your uploaded images</li>
            </ul>
            <div className="delete-account-grace-period">
              <strong>30-Day Grace Period:</strong> You have 30 days to change your mind. To restore your account, email <a href="mailto:help@wanderingsock.com" style={{ color: 'var(--color-accent-secondary)', textDecoration: 'underline' }}>help@wanderingsock.com</a> with your account email. After 30 days, your data will be permanently deleted.
            </div>
          </div>

          <div className="delete-account-confirmation">
            <label htmlFor="delete-confirm-input">
              Type <strong>DELETE</strong> to confirm:
            </label>
            <input
              id="delete-confirm-input"
              type="text"
              value={confirmText}
              onChange={(e) => {
                setConfirmText(e.target.value);
                setError(null);
              }}
              placeholder="Type DELETE"
              disabled={isDeleting}
              autoComplete="off"
            />
          </div>

          {error && (
            <div className="delete-account-error">
              {error}
            </div>
          )}
        </div>

        <div className="delete-account-modal-footer">
          <button
            className="delete-account-cancel-btn"
            onClick={handleClose}
            disabled={isDeleting}
          >
            Cancel
          </button>
          <button
            className="delete-account-confirm-btn"
            onClick={handleConfirm}
            disabled={isDeleting || confirmText !== 'DELETE'}
          >
            {isDeleting ? 'Deleting...' : 'Delete Account'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DeleteAccountModal;
